// src/pages/Jobs/JobCardGrid.tsx
import React from 'react';
import { Row, Col } from 'reactstrap';
import JobCard from './JobCard';
import JobCardGridSkeleton from './JobCardGridSkeleton';
import { Job } from '../../models/Job.interface';

interface JobCardGridProps {
  jobs: Job[];
  loading: boolean;
  highlightedJobId?: string;
  onJobHover?: (job: Job | null) => void;
  onJobClick?: (job: Job) => void;
  showDistance?: boolean;
  userLocation?: {
    latitude: number;
    longitude: number;
  };
  className?: string;
  style?: React.CSSProperties;
}

const JobCardGrid: React.FC<JobCardGridProps> = ({
  jobs,
  loading,
  highlightedJobId,
  onJobHover,
  onJobClick,
  showDistance = false,
  userLocation,
  className = '',
  style = {}
}) => {
  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Radius of Earth in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getJobDistance = (job: Job): number | undefined => {
    if (!showDistance || !userLocation || !job.propertyAddress?.latitude || !job.propertyAddress?.longitude) {
      return undefined;
    }
    
    return calculateDistance(
      userLocation.latitude,
      userLocation.longitude,
      job.propertyAddress.latitude,
      job.propertyAddress.longitude
    );
  };

  // Show skeleton while loading
  if (loading) {
    return <JobCardGridSkeleton />;
  }

  // Show empty state
  if (jobs.length === 0) {
    return (
      <div className="text-center py-5 bg-light rounded">
        <h5 className="mb-3">No jobs found</h5>
        <p className="text-muted mb-0">
          Try adjusting your search criteria or check back later for new opportunities.
        </p>
      </div>
    );
  }

  // Enhanced: Add scrollable container style with better UX
  const containerStyle: React.CSSProperties = {
    ...style,
    maxHeight: style.maxHeight || '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    // Enhanced scrollbar styling
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e0 #f7fafc',
    // Add padding to prevent clipping of rounded corners and hover effects
    padding: '12px 0',
    ...style
  };

  return (
    <div 
      className={`job-card-grid-container ${className}`}
      style={containerStyle}
    >
      <Row className="g-3" style={{ margin: '0 -0.5rem' }}>
        {jobs.map(job => {
          const distance = getJobDistance(job);
          return (
            <Col key={job.id} style={{minWidth:"380px", width:"400px", maxWidth:"420px", flexShrink: 0}}>
              <JobCard
                job={job}
                isHighlighted={job.id === highlightedJobId}
                onHover={onJobHover}
                onClick={onJobClick}
                showDistance={showDistance}
                distance={distance}
              />
            </Col>
          );
        })}
      </Row>

      {/* Add some padding at the bottom for better scrolling UX */}
      <div style={{ height: '20px' }}></div>

      {/* Enhanced: Add CSS styles for better UX */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .job-card-grid-container::-webkit-scrollbar {
            width: 8px;
          }
          
          .job-card-grid-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          
          .job-card-grid-container::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 4px;
          }
          
          .job-card-grid-container::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }
          
          .job-card-grid-container {
            scroll-behavior: smooth;
          }
          
          .job-card:hover {
            transform: translateY(-8px) scale(1.02) !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1) !important;
            z-index: 10;
            position: relative;
            margin: 0 !important; /* Prevent margin changes on hover */
          }
          
          .job-card:hover::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(45deg, #6366f1, #8b5cf6, #06b6d4);
            border-radius: 18px;
            z-index: -1;
            opacity: 0.1;
            animation: glow 2s ease-in-out infinite alternate;
          }
          
          @keyframes glow {
            from {
              opacity: 0.1;
            }
            to {
              opacity: 0.2;
            }
          }
          
          .job-card img {
            transition: opacity 0.15s ease-in-out, transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          .job-card:hover img {
            transform: scale(1.05);
          }
          
          .job-card img:not([src]) {
            opacity: 0;
          }
          
          .job-card {
            transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
            will-change: transform, box-shadow;
          }
        `
      }} />
    </div>
  );
};

export default JobCardGrid;